import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import type { UnlockedEntry } from '../qr/types';
import { useNav } from '../stores/navigation';
import { getXR8 } from '../ar/8thwall-loader';
import {
  createCharacterPipelineModule,
  type ArHandles,
  type ArMode,
} from '../ar/character-pipeline';
import { installStreamTracker, stopAllCameraStreams } from '../ar/camera-selector';

// 8th Wall の Threejs pipeline は window.THREE を要求する。
// ESM で import している THREE をグローバルに公開する。
(window as unknown as { THREE: typeof THREE }).THREE = THREE;

type Props = { entry: UnlockedEntry };

type Phase =
  | { kind: 'init' }
  | { kind: 'loading' }
  | { kind: 'placement'; arMode: ArMode }
  | { kind: 'capturing' }
  | { kind: 'preview'; dataUrl: string }
  | { kind: 'error'; msg: string };

type SessionRef = {
  XR8: any;
  moduleName: string;
  handles: ArHandles;
};

export function ArScreen({ entry }: Props) {
  const go = useNav((s) => s.go);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sessionRef = useRef<SessionRef | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'init' });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    setPhase({ kind: 'loading' });

    // canvas サイズは XRExtras.FullWindowCanvas が面倒を見る（自前 resize 不要）。

    // iOS デフォルトの背面 1x カメラを使う。レンズ強制パッチは iOS で
    // getUserMedia 失敗を誘発するケースがあるため無効化。
    // ストリーム追跡だけ入れて AR 終了時に確実に停止できるようにする。
    installStreamTracker();

    (async () => {
      try {
        const XR8 = (await getXR8()) as any;
        const XRExtras = (window as unknown as { XRExtras?: any }).XRExtras;
        if (cancelled) return;

        const { module: pipelineModule, handles } = createCharacterPipelineModule({
          params: entry,
          onModeChange: (arMode) => {
            console.log('[AR] onModeChange', arMode);
            setPhase((p) => (p.kind === 'placement' ? { kind: 'placement', arMode } : p));
          },
        });

        const modules = [
          XR8.GlTextureRenderer.pipelineModule(),
          XR8.Threejs.pipelineModule(),
          XR8.XrController.pipelineModule(),
          XR8.CanvasScreenshot.pipelineModule(),
        ];
        if (XRExtras) {
          // canvas を全画面に保つ + SLAM ローディング表示 + ランタイムエラー処理
          modules.push(XRExtras.FullWindowCanvas.pipelineModule());
          modules.push(XRExtras.Loading.pipelineModule());
          modules.push(XRExtras.RuntimeError.pipelineModule());
        } else {
          console.warn('[AR] XRExtras not loaded - canvas size may be wrong');
        }
        modules.push(pipelineModule);
        XR8.addCameraPipelineModules(modules);

        XR8.run({
          canvas,
          allowedDevices: XR8.XrConfig.device().MOBILE_AND_HEADSETS,
        });

        sessionRef.current = { XR8, moduleName: pipelineModule.name, handles };
        console.log('[AR] phase → placement(searching)');
        setPhase({ kind: 'placement', arMode: 'searching' });
      } catch (err) {
        if (cancelled) return;
        console.error('[AR] init failed', err);
        setPhase({ kind: 'error', msg: String((err as Error).message ?? err) });
      }
    })();

    return () => {
      cancelled = true;
      const s = sessionRef.current;
      if (s) {
        try {
          s.XR8.stop();
        } catch (e) {
          console.warn('[AR] stop failed', e);
        }
        try {
          s.XR8.removeCameraPipelineModule(s.moduleName);
        } catch (e) {
          console.warn('[AR] removeCameraPipelineModule failed', e);
        }
        sessionRef.current = null;
      }

      // FullWindowCanvas が canvas を document.body 直下に移動するため、
      // unmount 時に React が片付けてくれない。残ると home 画面を覆ってしまう。
      const c = canvas;
      if (c && c.parentElement === document.body) {
        try {
          c.parentElement.removeChild(c);
        } catch (e) {
          console.warn('[AR] canvas remove failed', e);
        }
      }

      // カメラストリームも明示的に停止
      stopAllCameraStreams();
    };
  }, [entry.m, entry.b, entry.f, entry.h, entry.w, entry.e]);

  function handleShutter() {
    const s = sessionRef.current;
    if (!s) return;
    setPhase({ kind: 'capturing' });
    // UI を一瞬隠してから撮影（次フレームでキャプチャ）
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        try {
          const b64: string = await s.XR8.CanvasScreenshot.takeScreenshot();
          const rawDataUrl = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
          const composedDataUrl = await composeWithLogo(rawDataUrl);
          setPhase({ kind: 'preview', dataUrl: composedDataUrl });
        } catch (err) {
          console.error('[AR] screenshot failed', err);
          setPhase({ kind: 'placement', arMode: s.handles.getMode() });
        }
      });
    });
  }

  function handleRetake() {
    console.log('[AR] retake clicked');
    const s = sessionRef.current;
    const m = s?.handles.getMode() ?? 'searching';
    setPhase({ kind: 'placement', arMode: m });
  }

  function handleReset() {
    console.log('[AR] reset clicked');
    sessionRef.current?.handles.reset();
  }

  async function handleSaveOrShare() {
    console.log('[AR] save/share clicked');
    if (phase.kind !== 'preview') return;
    try {
      const blob = await dataUrlToBlob(phase.dataUrl);
      const file = new File([blob], buildFileName(entry), { type: 'image/jpeg' });
      // iOS Safari は files のみ渡したときに「写真に保存」を共有シートに
      // 出してくれる。text や title を入れるとテキスト共有扱いになって
      // 「写真に保存」が消えるので、files 単体で渡す。
      const data: ShareData = { files: [file] };
      if (navigator.canShare?.(data)) {
        await navigator.share(data);
      } else if (navigator.share) {
        // 古い実装で files を扱えない場合はテキストのみ共有
        await navigator.share({ title: 'MarbleScramble AR' });
      } else {
        // デスクトップ等のフォールバック: ダウンロード
        downloadDataUrl(phase.dataUrl, file.name);
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') console.warn('[AR] save/share failed', err);
    }
  }

  function handleClose() {
    console.log('[AR] close clicked');
    // 詳細画面に戻ると Three.js レンダラ再構築で WebGL コンテキストが競合
    // して固まることがあるため、ホーム画面に戻す。
    go({ kind: 'home' });
  }

  // ---- レンダリング ----
  const isCapturing = phase.kind === 'capturing';
  const showPreview = phase.kind === 'preview';
  const arMode: ArMode | null = phase.kind === 'placement' ? phase.arMode : null;

  // FullWindowCanvas が canvas を document.body に移動するため、UI も body 直下に
  // ポータルでレンダリングする。これで XRExtras (z-index 999) より上に乗せられる。
  return createPortal(
    <div style={fullStyle}>
      <canvas
        ref={canvasRef}
        id="camerafeed"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          background: '#000',
          zIndex: 0,
          pointerEvents: 'auto',
        }}
      />

      {/* 撮影中の白フラッシュ */}
      {isCapturing && (
        <div style={{ ...absFill, background: '#fff', opacity: 0.9, zIndex: Z_FLASH }} />
      )}

      {/* プレースメント時の HUD */}
      {arMode && !showPreview && !isCapturing && (
        <>
          <button onClick={handleClose} style={topLeftBtn}>✕</button>
          <button onClick={handleReset} style={topRightBtn} disabled={arMode === 'searching'}>
            ↻
          </button>

          {arMode === 'searching' && (
            <div style={statusStyle}>
              ゆっくりスマホを動かして床/机を映してください
            </div>
          )}
          {arMode === 'placed' && (
            <div style={statusStyle}>
              ピンチ拡大 / ドラッグ回転 / ダブルタップでアニメ切替
            </div>
          )}

          <button
            onClick={handleShutter}
            disabled={arMode !== 'placed'}
            style={{
              ...shutterStyle,
              background: arMode === 'placed' ? '#fff' : 'rgba(255,255,255,0.35)',
              borderColor: arMode === 'placed' ? '#fff' : 'rgba(255,255,255,0.85)',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.5)',
            }}
            aria-label="撮影"
          />
        </>
      )}

      {/* ロード中 */}
      {phase.kind === 'loading' && (
        <div style={overlayCenterStyle}>
          <p>AR エンジン起動中…</p>
          <p className="muted" style={{ fontSize: 12 }}>
            カメラ権限を許可してください
          </p>
        </div>
      )}

      {/* エラー */}
      {phase.kind === 'error' && (
        <div style={overlayCenterStyle}>
          <p>AR を開始できませんでした</p>
          <p className="muted" style={{ fontSize: 13 }}>{phase.msg}</p>
          <button className="btn btn-secondary" onClick={handleClose} style={{ marginTop: 16 }}>
            戻る
          </button>
        </div>
      )}

      {/* プレビュー */}
      {showPreview && phase.kind === 'preview' && (
        <div
          style={{
            ...absFill,
            background: '#000',
            zIndex: Z_PREVIEW,
            pointerEvents: 'auto',
          }}
        >
          <button onClick={handleClose} style={topLeftBtn}>✕</button>
          <img
            src={phase.dataUrl}
            alt="captured"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              padding: '0 12px',
              zIndex: Z_PREVIEW + 1,
              pointerEvents: 'auto',
            }}
          >
            <button
              className="btn btn-secondary"
              onClick={handleRetake}
              style={{ pointerEvents: 'auto' }}
            >
              ↺ 撮り直し
            </button>
            <button
              className="btn"
              onClick={handleSaveOrShare}
              style={{ pointerEvents: 'auto' }}
            >
              📥 保存 / 共有
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ---- helpers ----

const LOGO_URL = '/assets/images/title.png';

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/**
 * 撮影画像の右下にタイトルロゴを焼き込む。
 * 失敗時は元画像をそのまま返す（合成はあくまで装飾）。
 */
async function composeWithLogo(screenshotDataUrl: string): Promise<string> {
  try {
    const [shot, logo] = await Promise.all([
      loadImage(screenshotDataUrl),
      loadImage(LOGO_URL),
    ]);
    const canvas = document.createElement('canvas');
    canvas.width = shot.naturalWidth;
    canvas.height = shot.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return screenshotDataUrl;

    ctx.drawImage(shot, 0, 0);

    // ロゴサイズ: 画像幅の 28%、ただし高さ 14% を超えない
    const maxW = canvas.width * 0.28;
    const maxH = canvas.height * 0.14;
    const aspect = logo.naturalWidth / logo.naturalHeight;
    let lw = maxW;
    let lh = lw / aspect;
    if (lh > maxH) {
      lh = maxH;
      lw = lh * aspect;
    }
    const margin = canvas.width * 0.025;
    const lx = canvas.width - lw - margin;
    const ly = canvas.height - lh - margin;

    // 視認性のため、ロゴ背後に半透明の白いソフトシャドウを置く
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = canvas.width * 0.008;
    ctx.shadowOffsetY = canvas.width * 0.003;
    ctx.drawImage(logo, lx, ly, lw, lh);
    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.92);
  } catch (err) {
    console.warn('[AR] logo compose failed', err);
    return screenshotDataUrl;
  }
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function buildFileName(entry: UnlockedEntry): string {
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  return `marble_ar_m${entry.m}_${entry.b}_${ts}.jpg`;
}

// ---- styles (inline) ----

const fullStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  // background は黒のままだが canvas が移動した後は前面のレイヤなので透過させる
  background: 'transparent',
  overflow: 'hidden',
  // タップが下のキャンバスに届くようにするため、ラッパは透過。
  // 個別ボタンには pointerEvents: 'auto' を入れて拾う。
  pointerEvents: 'none',
  zIndex: 9001,
};

const absFill: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
};

// XRExtras が z-index 800〜999 を使っているため、UI は 9000 番台に乗せる
const Z_HUD = 9001;
const Z_HUD_TOP = 9002;
const Z_OVERLAY = 9000;
const Z_PREVIEW = 9100;
const Z_FLASH = 9200;

const topLeftBtn: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
  left: 12,
  width: 44,
  height: 44,
  borderRadius: 22,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  fontSize: 20,
  zIndex: Z_HUD_TOP,
  pointerEvents: 'auto',
};

const topRightBtn: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
  right: 12,
  width: 44,
  height: 44,
  borderRadius: 22,
  background: 'rgba(0,0,0,0.5)',
  color: '#fff',
  fontSize: 20,
  zIndex: Z_HUD_TOP,
  pointerEvents: 'auto',
};

const statusStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  padding: '8px 16px',
  borderRadius: 20,
  fontSize: 13,
  whiteSpace: 'nowrap',
  zIndex: Z_HUD,
  pointerEvents: 'none',
};

const shutterStyle: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
  transform: 'translateX(-50%)',
  width: 'clamp(72px, 18vw, 96px)',
  height: 'clamp(72px, 18vw, 96px)',
  borderRadius: '50%',
  border: '4px solid rgba(255,255,255,0.7)',
  background: '#fff',
  zIndex: Z_HUD,
  pointerEvents: 'auto',
};

const overlayCenterStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  textAlign: 'center',
  padding: 16,
  zIndex: Z_OVERLAY,
  background: 'rgba(0,0,0,0.5)',
  pointerEvents: 'auto',
};
